<template>
  <div>
    <div class="d-flex">
      <h5 v-if="isMobile">
        <b>General Information</b>
      </h5>
      <h3 v-else>
        <b>General Information</b>
      </h3>
      <div class="ml-5">
        <div v-if="editing">
          <b-link class="buttontext-2" @click="resetForm">Cancel</b-link>
          <b-link class="buttontext ml-4" @click="onSubmit">Save</b-link>
        </div>
        <b-link v-else-if="canUpdate" @click="onClickUpdate">
          <b-img
            width="16"
            class="rounded-0 mr-2 mb-1"
            :src="require('@/assets/icons/edit.svg')"
          ></b-img>
          <span class="buttontext">Update</span>
        </b-link>
      </div>
    </div>

    <b-row class="mt-2" :class="rowClass">
      <b-col>
        <b-row :class="rowClass">
          <b-col>
            <b-form-group id="input-group-board-name" class="col-12">
              <i
                class="clearable"
                v-show="form.board_name && editing"
                @click="form.board_name = ''"
                >&times;</i
              >

              <b-form-input
                id="input-board-name"
                v-model="form.board_name"
                class="input-effect"
                :class="form.board_name ? 'has-content' : ''"
                type="text"
                required
                aria-describedby="input-board-name-feedback"
                :disabled="!editing"
              ></b-form-input>
              <label>Board name</label>

              <div class="input-error" v-if="form.$getError('board_name')">
                {{ form.$getError('board_name')[0] }}
              </div>
            </b-form-group>

            <b-form-group id="input-group-location" class="col-12">
              <i
                class="clearable"
                v-show="form.location && editing"
                @click="form.location = ''"
                >&times;</i
              >

              <b-form-input
                id="input-location"
                v-model="form.location"
                class="input-effect"
                :class="form.location ? 'has-content' : ''"
                type="text"
                required
                aria-describedby="input-location-feedback"
                :disabled="!editing"
              ></b-form-input>
              <label>Location</label>

              <div class="input-error" v-if="form.$getError('location')">
                {{ form.$getError('location')[0] }}
              </div>
            </b-form-group>
          </b-col>

          <b-col>
            <b-form-group id="input-group-cable-size" class="col-12">
              <i
                class="clearable"
                v-show="form.supply_cable_size && editing"
                @click="form.supply_cable_size = ''"
                >&times;</i
              >

              <b-form-input
                id="input-cable-size"
                v-model="form.supply_cable_size"
                class="input-effect"
                :class="form.supply_cable_size ? 'has-content' : ''"
                type="text"
                required
                aria-describedby="input-cable-size-feedback"
                :disabled="!editing"
              ></b-form-input>
              <label>Supply cable size</label>

              <div
                class="input-error"
                v-if="form.$getError('supply_cable_size')"
              >
                {{ form.$getError('supply_cable_size')[0] }}
              </div>
            </b-form-group>

            <b-form-group id="input-group-earth-location" class="col-12">
              <i
                class="clearable"
                v-show="form.main_earth_location && editing"
                @click="form.main_earth_location = ''"
                >&times;</i
              >

              <b-form-input
                id="input-earth-location"
                v-model="form.main_earth_location"
                class="input-effect"
                :class="form.main_earth_location ? 'has-content' : ''"
                type="text"
                required
                aria-describedby="input-earth-location-feedback"
                :disabled="!editing"
              ></b-form-input>
              <label>Main Earth location</label>

              <div
                class="input-error"
                v-if="form.$getError('main_earth_location')"
              >
                {{ form.$getError('main_earth_location')[0] }}
              </div>
            </b-form-group>
          </b-col>

          <b-col v-if="board">
            <div class="mb-3">
              <b-form-group id="input-group-meter-2" class="col-12">
                <i
                  class="clearable"
                  v-show="form.supplied_from && editing && !board.parent_id"
                  @click="form.supplied_from = ''"
                  >&times;</i
                >

                <b-form-input
                  v-model="form.supplied_from"
                  class="input-effect"
                  :class="form.supplied_from ? 'has-content' : ''"
                  type="text"
                  required
                  aria-describedby="input-meter-2-feedback"
                  :disabled="!editing || board.parent_id"
                ></b-form-input>
                <label>Supplied from</label>

                <div class="input-error" v-if="form.$getError('supplied_from')">
                  {{ form.$getError('supplied_from')[0] }}
                </div>
              </b-form-group>
            </div>

            <div class="text-right text-secondary">Installed by</div>
            <div class="text-right font-weight-bold">
              {{ board.installed_by }}
            </div>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Form from '@/utils/form'

export default {
  props: {
    canUpdate: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    ...mapState({
      board: state => state.board.boardDetails
    }),

    rowClass() {
      return this.isMobile ? 'd-block' : ''
    }
  },

  data() {
    return {
      loading: false,
      editing: false,
      form: new Form({
        board_id: null,
        board_name: null,
        supply_cable_size: null,
        location: null,
        supplied_from: null,
        main_earth_location: null
      })
    }
  },

  methods: {
    ...mapActions({
      updateBoard: 'board/updateBoard'
    }),

    onClickUpdate() {
      this.editing = true
    },

    resetForm() {
      this.editing = false
      for (let key in this.form) {
        this.form[key] = this.board[key]
      }
      this.form.$clearErrors()
    },

    formHasErrors() {
      const requiredField = 'This field is required.'
      this.form.$setErrors({
        board_name: this.form.board_name ? '' : [requiredField],
        location: this.form.location ? '' : [requiredField]
      })

      return this.form.$hasErrors()
    },

    async onSubmit() {
      if (!this.formHasErrors()) {
        this.loading = true
        await this.updateBoard({
          board_id: this.form.board_id,
          supplied_from: this.form.supplied_from,
          board_name: this.form.board_name,
          supply_cable_size: this.form.supply_cable_size,
          location: this.form.location,
          main_earth_location: this.form.main_earth_location
        })
          .then(() => {
            this.editing = false
          })
          .catch(error => {
            this.form.$handleError(error)
          })
        this.loading = false
      }
    }
  },

  created() {
    this.$root.$on('boardLoaded', () => {
      this.resetForm()
    })
  }
}
</script>

<style lang="scss" scoped>
.form-control:disabled,
.form-control[readonly] {
  background-color: #f8f9fa;
}
.buttontext {
  font-size: 14px;
  font-weight: bold;
}
.buttontext-2 {
  color: grey;
  font-size: 14px;
}
.b-link {
}
</style>
